import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Col, Row } from 'react-bootstrap'
import FullLogo from '../assets/logos/full-logo.png'
import ProfileAvatar from '../components/profileAvatar'
import theme from '../constants/theme'
import {
  FooterContent,
  Typography,
  InfoCard,
  HeroBannerSection,
  HeroBannerTitle,
  HeroBannerMetaRow,
  HeroBannerMetaItem,
  HeroBannerTagsRow,
  HeroBannerTag,
  HeroBannerActionsCol,
  HeroBannerApplyButton,
  BannerDecorCircleLarge,
  BannerDecorCircleSmall,
  BannerContentLayout,
  BannerTitleArea,
  BannerAgencyRow,
  BannerAgencyText,
  BlockTypography,
  JobInfoCol,
  CompanyDetailsCol,
  PartnerCard,
  PartnerCardContent,
  PartnerCardLabel,
  VerifiedBadge,
  CardSectionHeader,
  CardIconBox,
  SectionSubLabel,
  ExperienceSubCard,
  ExperienceIconCircle,
  FieldMicroLabel,
  RequirementsGrid,
  RequirementsGridCell,
  RequirementsIconCircle,
  RequirementsFieldLabel,
  SideCard,
  ProfileCardHeader,
  CompanyLogoImg,
  CompanyDivider,
  DetailRowsList,
  DetailRow,
  DetailLabel,
  DetailValueLink,
  ArraySectionWrapper,
  ArraySectionTitle,
  ArrayCountBadge,
  ArrayPillsRow,
  ArrayPillItem,
} from '../components/styled'
import {
  seniorityLevel,
  employmentType,
  educationLevel,
  travelFreq,
  languages,
} from '../constants/position'
import {
  formatCountryName,
  formatWebsiteUrl,
} from '../utilities/stringUtilities'
import LanguagesDropdown from '../components/languageDropdown'
import House from '../assets/svgs/House'
import GeoAlt from '../assets/svgs/GeoAlt'
import Mortarboard from '../assets/svgs/Mortarboard'
import Clock from '../assets/svgs/clock'
import CheckSquare from '../assets/svgs/CheckSquare'
import Briefcase from '../assets/svgs/Briefcase'
import BadgeCheck from '../assets/svgs/BadgeCheck'
import FileEarmarkText from '../assets/svgs/FileEarmarkText'
import Translate from '../assets/svgs/Translate'
import Plane from '../assets/svgs/Plane'
import Trophy from '../assets/svgs/Trophy'
import BookHalf from '../assets/svgs/BookHalf'
import CreditCard from '../assets/svgs/CreditCard'
import Star from '../assets/svgs/Star'
import Globe2 from '../assets/svgs/Globe2'
import PeopleFill from '../assets/svgs/PeopleFill'
import CurrencyDollar from '../assets/svgs/CurrencyDollar'
import EmploymentType from '../assets/svgs/EmploymentType'
import UserLock from '../assets/svgs/UserLock'

function JobApplication({
  onApplyClick,
  company,
  position,
  isEasyApply = false,
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n?.language
  const handleClick = () => {
    onApplyClick()
  }

  const mapValueToLabel = (constantsList, value) => {
    const selectedField = constantsList?.find((field) => field.value === value)
    return selectedField ? selectedField[locale] : ''
  }

  const getYearsOfExperienceText = () => {
    let yearsOfExperience = t('pages.minExpYears', {
      minYears: position?.yearsOfExperience,
    })
    if (position?.maxYearsOfExperience)
      yearsOfExperience = t('pages.minMaxExpYears', {
        minYears: position?.yearsOfExperience,
        maxYears: position?.maxYearsOfExperience,
      })
    if (position?.yearsOfExperience === 0)
      yearsOfExperience = t('pages.noExpNeeded')
    return <Typography size="13px">{yearsOfExperience}</Typography>
  }

  const positionArrayLabels = [
    { value: 'certificates', label: t('pages.certificates'), Icon: Trophy },
    { value: 'knowledge', label: t('pages.knowledge'), Icon: BookHalf },
    { value: 'licenses', label: t('pages.licenses'), Icon: CreditCard },
    { value: 'skills', label: t('pages.skills'), Icon: Star },
  ]

  const positionDetailFields = [
    {
      id: 'language',
      label: t('pages.language'),
      value: Array.isArray(position?.language)
        ? position.language
            .map((lang) => mapValueToLabel(languages, lang))
            .filter(Boolean)
            .join(', ')
        : mapValueToLabel(languages, position?.language),
      Icon: Translate,
    },
    {
      id: 'educationLevel',
      label: t('pages.educationLevel'),
      value: mapValueToLabel(educationLevel, position?.educationLevel),
      Icon: Mortarboard,
    },
    {
      id: 'travelFreq',
      label: t('pages.travelFreq'),
      value: mapValueToLabel(travelFreq, position?.travelFreq),
      Icon: Plane,
    },
    {
      id: 'location',
      label: t('pages.location'),
      value: formatCountryName(position?.country),
      Icon: GeoAlt,
    },
    !position?.hideSalary &&
      position?.budget && {
        id: 'salary',
        label: t('pages.salary'),
        value: `${position.budget} ${position.currency}`,
        Icon: CurrencyDollar,
      },
    !position?.hideSalary &&
      position?.additionalSalary && {
        id: 'additionalSalary',
        label: t('pages.additionalSalary'),
        value: `${position.additionalSalary} `,
        Icon: CurrencyDollar,
      },
    {
      id: 'workplaceType',
      label: t('pages.workplaceType'),
      value: position?.workplaceType,
      Icon: Briefcase,
    },
    {
      id: 'employmentType',
      label: t('pages.employmentType'),
      value: position?.employmentType,
      Icon: EmploymentType,
    },
    position?.employmentType === 'Project-Based' && {
      id: 'projectDuration',
      label: t('pages.projectDuration'),
      value: `${position?.projectDuration?.number} ${position?.projectDuration?.unit}`,
      Icon: Clock,
    },
    {
      id: 'seniorityLevel',
      label: t('pages.seniorityLevel'),
      value: position?.seniorityLevel,
      Icon: UserLock,
    },
  ].filter((f) => f && f.value)

  const getApplyButtonText = () => {
    if (isEasyApply) return t('pages.easyApply')
    if (position?.isInternalApply) return t('pages.applyWithCompanyID')
    return t('pages.apply')
  }

  return (
    <div style={{ backgroundColor: '#F1F5FA', overflow: 'clip' }}>
      <Row
        style={{
          position: 'sticky',
          top: 0,

          zIndex: 1000,
          backgroundColor: theme.white,
          padding: '0px 20px',
        }}
      >
        <Col xs="auto">
          <img height="auto" width="150px" src={FullLogo} alt="" />
        </Col>
        <Col />
        <Col xs="auto" className="mt-3">
          <LanguagesDropdown />
        </Col>
      </Row>

      <Row>
        <div className="h-100 mb-5 px-3 px-sm-3 px-md-4 px-lg-5">
          <Row className="mx-0 mt-4 mb-4">
            <Col className="px-0">
              <HeroBannerSection>
                <BannerDecorCircleLarge />
                <BannerDecorCircleSmall />
                <BannerContentLayout>
                  <BannerTitleArea>
                    <BannerAgencyRow>
                      <House
                        width="13"
                        height="13"
                        color="rgba(255,255,255,0.8)"
                      />
                      <BannerAgencyText>
                        {position?.agency?.name ||
                          (company?.showClientDetails !== false
                            ? company?.companyName
                            : null)}{' '}
                        {t('pages.isHiringNow')}
                      </BannerAgencyText>
                    </BannerAgencyRow>

                    <HeroBannerTitle>{position?.title}</HeroBannerTitle>

                    <HeroBannerMetaRow>
                      {position?.country && (
                        <HeroBannerMetaItem>
                          <GeoAlt
                            width="13"
                            height="13"
                            color="rgba(255,255,255,0.85)"
                          />
                          {formatCountryName(position.country)}
                        </HeroBannerMetaItem>
                      )}
                      {position?.educationLevel && (
                        <HeroBannerMetaItem>
                          <Mortarboard
                            width="13"
                            height="13"
                            color="rgba(255,255,255,0.85)"
                          />
                          {mapValueToLabel(
                            educationLevel,
                            position.educationLevel,
                          )}
                        </HeroBannerMetaItem>
                      )}
                      {position?.travelFreq && (
                        <HeroBannerMetaItem>
                          <Plane
                            width="13"
                            height="13"
                            color="rgba(255,255,255,0.85)"
                          />
                          {mapValueToLabel(travelFreq, position.travelFreq)}
                        </HeroBannerMetaItem>
                      )}
                    </HeroBannerMetaRow>

                    <HeroBannerTagsRow>
                      {position?.employmentType && (
                        <HeroBannerTag>
                          {mapValueToLabel(
                            employmentType,
                            position.employmentType,
                          )}
                        </HeroBannerTag>
                      )}
                      {position?.seniorityLevel && (
                        <HeroBannerTag>
                          {mapValueToLabel(
                            seniorityLevel,
                            position.seniorityLevel,
                          )}
                        </HeroBannerTag>
                      )}
                    </HeroBannerTagsRow>
                  </BannerTitleArea>

                  <HeroBannerActionsCol>
                    <HeroBannerApplyButton onClick={handleClick}>
                      <CheckSquare width="15" height="15" />
                      {getApplyButtonText()}
                    </HeroBannerApplyButton>
                  </HeroBannerActionsCol>
                </BannerContentLayout>
              </HeroBannerSection>
            </Col>
          </Row>
          <Row className="mb-3 px-2">
            <JobInfoCol className="lh-sm mb-3 mb-lg-0" xs={12} lg={7}>
              {position?.agency && (
                <Row className="mb-3">
                  <PartnerCard>
                    <ProfileAvatar name={position.agency.name} size={3} />
                    <PartnerCardContent>
                      <PartnerCardLabel>
                        {t('pages.recruitmentPartner')}
                      </PartnerCardLabel>
                      <BlockTypography
                        bold
                        size="15px"
                        color={theme.talentinoBlack}
                      >
                        {position.agency.name}
                      </BlockTypography>
                      <Typography
                        size="12px"
                        regular
                        color={theme.talentinoSecondaryGray}
                      >
                        {t('pages.agencyTagline')}
                      </Typography>
                    </PartnerCardContent>
                    <VerifiedBadge>
                      <BadgeCheck width="11" height="11" />
                      {t('pages.verified')}
                    </VerifiedBadge>
                  </PartnerCard>
                </Row>
              )}

              {/* Job Description */}
              {position?.description && (
                <Row className="mb-3">
                  <InfoCard className="px-4 py-3 overflow-hidden">
                    <CardSectionHeader mb="14px">
                      <CardIconBox>
                        <FileEarmarkText
                          width="15"
                          height="15"
                          color={theme.brandColorOrange}
                        />
                      </CardIconBox>
                      <Typography bold size="16px" color={theme.talentinoBlack}>
                        {t('pages.rolesResponsibilities')}
                      </Typography>
                    </CardSectionHeader>
                    <SectionSubLabel>
                      {t('pages.responsibilities')}
                    </SectionSubLabel>
                    <div
                      className="fw-light"
                      dangerouslySetInnerHTML={{
                        __html: position?.description,
                      }}
                    />
                  </InfoCard>
                </Row>
              )}

              {/* Requirements */}

              <Row>
                <InfoCard className="px-4 py-3 overflow-hidden">
                  <CardSectionHeader>
                    <CardIconBox>
                      <CheckSquare
                        width="15"
                        height="15"
                        color={theme.brandColorOrange}
                      />
                    </CardIconBox>
                    <Typography bold size="16px" color={theme.talentinoBlack}>
                      {t('pages.requirements')}
                    </Typography>
                  </CardSectionHeader>

                  {position?.requirements && (
                    <>
                      <SectionSubLabel>
                        {t('pages.additionalRequirements')}
                      </SectionSubLabel>
                      <div
                        className="fw-light"
                        dangerouslySetInnerHTML={{
                          __html: position?.requirements,
                        }}
                      />
                    </>
                  )}
                </InfoCard>
              </Row>
              {position?.skills.length ? (
                <Row className="mt-3">
                  <InfoCard className="px-4 py-3 overflow-hidden">
                    <CardSectionHeader>
                      <CardIconBox>
                        <Star
                          width="15"
                          height="15"
                          color={theme.brandColorOrange}
                        />
                      </CardIconBox>
                      <Typography bold size="16px" color={theme.talentinoBlack}>
                        {t('pages.skillsKeywords')}
                      </Typography>
                      {positionArrayLabels.some(
                        (ak) => position[ak.value]?.length,
                      ) && (
                        <ArrayCountBadge style={{ marginLeft: 'auto' }}>
                          {t('pages.keywordsCount', {
                            count: positionArrayLabels.reduce(
                              (acc, ak) =>
                                acc + (position[ak.value]?.length || 0),
                              0,
                            ),
                          })}
                        </ArrayCountBadge>
                      )}
                    </CardSectionHeader>

                    <div>
                      {positionArrayLabels.some(
                        (ak) => position[ak.value]?.length,
                      ) && (
                        <div className="mt-3">
                          {positionArrayLabels
                            .filter((ak) => position[ak.value]?.length)
                            .map((ak, idx, arr) => (
                              <ArraySectionWrapper
                                key={ak.value}
                                isLast={idx === arr.length - 1}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '10px',
                                  }}
                                >
                                  <RequirementsIconCircle
                                    style={{ flexShrink: 0 }}
                                  >
                                    <ak.Icon
                                      width="13"
                                      height="13"
                                      color={theme.brandColorOrange}
                                    />
                                  </RequirementsIconCircle>
                                  <ArraySectionTitle style={{ flexShrink: 0 }}>
                                    {ak.label}
                                  </ArraySectionTitle>
                                  <ArrayPillsRow style={{ flex: 1 }}>
                                    {position[ak.value].map((item) => (
                                      <ArrayPillItem key={item}>
                                        {item}
                                      </ArrayPillItem>
                                    ))}
                                  </ArrayPillsRow>
                                </div>
                              </ArraySectionWrapper>
                            ))}
                        </div>
                      )}
                    </div>
                  </InfoCard>
                </Row>
              ) : (
                <></>
              )}
            </JobInfoCol>

            {/*  company profile */}
            <CompanyDetailsCol
              id="company-details"
              className="lh-sm"
              xs={12}
              lg={5}
              style={{
                position: 'sticky',
                top: '80px',
                alignSelf: 'flex-start',
              }}
            >
              {/* Job Details */}
              <Row>
                <InfoCard className="px-4 py-3 overflow-hidden">
                  <CardSectionHeader>
                    <CardIconBox>
                      <Briefcase
                        width="15"
                        height="15"
                        color={theme.brandColorOrange}
                      />
                    </CardIconBox>
                    <Typography bold size="16px" color={theme.talentinoBlack}>
                      {t('pages.positionDetails')}
                    </Typography>
                  </CardSectionHeader>

                  {/* Experience sub-card */}
                  {position?.yearsOfExperience !== undefined && (
                    <ExperienceSubCard>
                      <ExperienceIconCircle>
                        <Clock
                          width="14"
                          height="14"
                          color={theme.brandColorOrange}
                        />
                      </ExperienceIconCircle>
                      <div>
                        <FieldMicroLabel>
                          {t('pages.yearsOfExperience')}
                        </FieldMicroLabel>
                        <Typography
                          size="14px"
                          bold
                          color={theme.talentinoBlack}
                        >
                          {getYearsOfExperienceText()}
                        </Typography>
                      </div>
                    </ExperienceSubCard>
                  )}

                  {/* Requirements grid */}
                  <RequirementsGrid>
                    {positionDetailFields.map((field) => (
                      <RequirementsGridCell key={field.id}>
                        <RequirementsIconCircle>
                          <field.Icon
                            width="13"
                            height="13"
                            color={theme.brandColorOrange}
                          />
                        </RequirementsIconCircle>
                        <div>
                          <RequirementsFieldLabel>
                            {field.label}
                          </RequirementsFieldLabel>
                          <Typography
                            size="13px"
                            semiBold
                            color={theme.talentinoBlack}
                          >
                            {field.value}
                          </Typography>
                        </div>
                      </RequirementsGridCell>
                    ))}
                  </RequirementsGrid>
                </InfoCard>
              </Row>
              {company?.showClientDetails !== false && (
                <Row className="mt-3">
                  <SideCard>
                    <ProfileCardHeader>
                      {company?.logo ? (
                        <CompanyLogoImg src={company?.logo} alt="logo" />
                      ) : (
                        <ProfileAvatar name={company?.companyName} size={3} />
                      )}
                      <div>
                        <BlockTypography
                          bold
                          size="15px"
                          color={theme.brandColorOrange}
                        >
                          {company?.companyName}
                        </BlockTypography>
                      </div>
                    </ProfileCardHeader>

                    <DetailRowsList>
                      {[
                        {
                          id: 'location',
                          icon: (
                            <GeoAlt
                              width="14"
                              height="14"
                              color={theme.brandColorOrange}
                            />
                          ),
                          label: t('pages.location'),
                          value: company?.workingLocations?.[0]
                            ? formatCountryName(company?.workingLocations?.[0])
                            : null,
                        },
                        {
                          id: 'website',
                          icon: (
                            <Globe2
                              width="14"
                              height="14"
                              color={theme.brandColorOrange}
                            />
                          ),
                          label: t('general.website'),
                          value: company?.website,
                          isLink: true,
                          href: formatWebsiteUrl(company?.website),
                        },
                        {
                          id: 'industry',
                          icon: (
                            <Briefcase
                              width="14"
                              height="14"
                              color={theme.brandColorOrange}
                            />
                          ),
                          label: t('card.industry'),
                          value: company?.industry,
                        },
                        {
                          id: 'companySize',
                          icon: (
                            <PeopleFill
                              width="14"
                              height="14"
                              color={theme.brandColorOrange}
                            />
                          ),
                          label: t('card.companySize'),
                          value: company?.companySize,
                        },
                      ]
                        .filter((d) => d.value)
                        .map((detail, i, arr) => (
                          <DetailRow
                            key={detail.id}
                            isLast={i === arr.length - 1}
                          >
                            <RequirementsIconCircle>
                              {detail.icon}
                            </RequirementsIconCircle>
                            <DetailLabel>{detail.label}</DetailLabel>
                            {detail.isLink ? (
                              <DetailValueLink
                                href={detail.href}
                                target="_blank"
                                color={theme.brandColorOrange}
                              >
                                {detail.value}
                              </DetailValueLink>
                            ) : (
                              <Typography
                                size="13px"
                                bold
                                color={theme.talentinoBlack}
                              >
                                {detail.value}
                              </Typography>
                            )}
                          </DetailRow>
                        ))}
                    </DetailRowsList>
                  </SideCard>
                </Row>
              )}
              {position?.agency && (
                <Row className="mt-3">
                  <SideCard>
                    <ProfileCardHeader>
                      {position?.agency?.logo ? (
                        <CompanyLogoImg
                          src={position?.agency?.logo}
                          alt="logo"
                        />
                      ) : (
                        <ProfileAvatar name={position?.agency?.name} size={3} />
                      )}
                      <div>
                        <BlockTypography
                          bold
                          size="15px"
                          color={theme.brandColorOrange}
                        >
                          {position?.agency?.name}
                        </BlockTypography>
                        {(company?.isAgency || position?.agency) && (
                          <Typography
                            size="12px"
                            regular
                            color={theme.talentinoSecondaryGray}
                          >
                            {t('pages.recruitmentAgency')}
                          </Typography>
                        )}
                      </div>
                    </ProfileCardHeader>

                    <CompanyDivider />

                    <DetailRowsList>
                      {[
                        {
                          id: 'location',
                          icon: (
                            <GeoAlt
                              width="14"
                              height="14"
                              color={theme.brandColorOrange}
                            />
                          ),
                          label: t('pages.location'),
                          value: position?.agency?.workingLocations?.[0]
                            ? formatCountryName(
                                position?.agency?.workingLocations?.[0],
                              )
                            : null,
                        },
                        {
                          id: 'website',
                          icon: (
                            <Globe2
                              width="14"
                              height="14"
                              color={theme.brandColorOrange}
                            />
                          ),
                          label: t('general.website'),
                          value: position?.agency?.website,
                          isLink: true,
                          href: formatWebsiteUrl(position?.agency?.website),
                        },
                      ]
                        .filter((d) => d.value)
                        .map((detail, i, arr) => (
                          <DetailRow
                            key={detail.id}
                            isLast={i === arr.length - 1}
                          >
                            <RequirementsIconCircle>
                              {detail.icon}
                            </RequirementsIconCircle>
                            <DetailLabel>{detail.label}</DetailLabel>
                            {detail.isLink ? (
                              <DetailValueLink
                                href={detail.href}
                                target="_blank"
                                color={theme.brandColorOrange}
                              >
                                {detail.value}
                              </DetailValueLink>
                            ) : (
                              <Typography
                                size="13px"
                                bold
                                color={theme.talentinoBlack}
                              >
                                {detail.value}
                              </Typography>
                            )}
                          </DetailRow>
                        ))}
                    </DetailRowsList>
                  </SideCard>
                </Row>
              )}
            </CompanyDetailsCol>
          </Row>
        </div>
      </Row>

      <FooterContent className="d-flex py-2 border-t border-gray-200">
        {t('footer.TalentinoAI')} © {new Date().getFullYear()}{' '}
        {t('footer.allRightsReserved')}
      </FooterContent>
    </div>
  )
}

JobApplication.propTypes = {
  onApplyClick: PropTypes.func.isRequired,
  isEasyApply: PropTypes.bool,
  company: PropTypes.shape({
    companyName: PropTypes.string,
    website: PropTypes.string,
    workingLocations: PropTypes.arrayOf(PropTypes.string),
    address: PropTypes.string,
    industry: PropTypes.string,
    logo: PropTypes.string,
    showClientDetails: PropTypes.bool,
    companySize: PropTypes.string,
    isAgency: PropTypes.bool,
  }).isRequired,
  position: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    requirements: PropTypes.string,
    employmentType: PropTypes.string,
    isInternalApply: PropTypes.bool,
    seniorityLevel: PropTypes.string,
    workPlaceType: PropTypes.string,
    workplaceType: PropTypes.string,
    projectDuration: PropTypes.string,
    language: PropTypes.arrayOf(PropTypes.string),
    educationLevel: PropTypes.string,
    yearsOfExperience: PropTypes.number,
    maxYearsOfExperience: PropTypes.number,
    country: PropTypes.string,
    travelFreq: PropTypes.string,
    hideSalary: PropTypes.bool,
    currency: PropTypes.string,
    budget: PropTypes.string,
    additionalSalary: PropTypes.string,
    skills: PropTypes.arrayOf(PropTypes.string),
    agency: PropTypes.shape({
      name: PropTypes.string,
      logo: PropTypes.string,
      workingLocations: PropTypes.arrayOf(PropTypes.string),
      website: PropTypes.string,
    }),
  }).isRequired,
}

export default JobApplication
